import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ChartBarIcon,
  UserGroupIcon,
  CalendarIcon,
  DocumentArrowDownIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  UsersIcon
} from '@heroicons/react/24/outline';

const LeagueReports = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('tryouts'); // 'tryouts' | 'events' | 'invoices'
  const [loading, setLoading] = useState(false);

  // Tryout registrations state
  const [tryoutData, setTryoutData] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [organizations, setOrganizations] = useState([]);

  // Events by age group state
  const [eventsByAgeGroup, setEventsByAgeGroup] = useState([]);
  const [orgFilter, setOrgFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Outstanding AR state
  const [arSummary, setArSummary] = useState(null);
  const [arByAgeGroup, setArByAgeGroup] = useState([]);
  const [arByTeam, setArByTeam] = useState([]);
  const [arByType, setArByType] = useState([]);
  const [openInvoicesByMonth, setOpenInvoicesByMonth] = useState([]);
  const [arInvoices, setArInvoices] = useState([]);
  const [openInvoicesOrg, setOpenInvoicesOrg] = useState('');

  // Users report state
  const [usersReport, setUsersReport] = useState([]);
  const [usersReportAgeGroups, setUsersReportAgeGroups] = useState([]);
  const [usersReportOrg, setUsersReportOrg] = useState('');
  const [usersReportAgeGroup, setUsersReportAgeGroup] = useState('');

  useEffect(() => {
    if (!user?.token) return;
    fetchOrganizations();
  }, [user?.token]);

  useEffect(() => {
    if (!user?.token) return;
    if (activeTab === 'tryouts') {
      fetchTryoutRegistrations();
    } else if (activeTab === 'events') {
      fetchEventsByAgeGroup();
    } else if (activeTab === 'invoices') {
      fetchOpenInvoicesByMonth();
    } else if (activeTab === 'users') {
      fetchUsersReport();
    }
  }, [user?.token, activeTab, selectedOrg, selectedEvent, selectedAgeGroup, selectedStatus, orgFilter, startDate, endDate, openInvoicesOrg, usersReportOrg, usersReportAgeGroup]);

  const fetchOrganizations = async () => {
    if (!user?.token) return;
    try {
      const response = await axios.get('/api/organizations/my-organizations', {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      const orgs = Array.isArray(response.data) ? response.data : [];
      const transformed = orgs.map(orgUser => {
        if (orgUser.Organization) {
          return {
            id: orgUser.Organization.id,
            name: orgUser.Organization.name
          };
        }
        return orgUser;
      });
      setOrganizations(transformed);
      if (transformed.length > 0 && !selectedOrg) {
        setSelectedOrg(transformed[0].id);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations');
    }
  };

  const fetchTryoutRegistrations = async () => {
    if (!user?.token || !selectedOrg) return;

    try {
      setLoading(true);
      const params = {
        organizationId: selectedOrg,
        ageGroup: selectedAgeGroup || undefined,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        eventId: selectedEvent || undefined
      };

      const response = await axios.get('/api/reports/tryout-registrations', {
        params,
        headers: { Authorization: `Bearer ${user.token}` }
      });
      
      setTryoutData(response.data.tryouts || []);
    } catch (error) {
      console.error('Error fetching tryout registrations:', error);
      toast.error('Failed to load tryout registrations');
    } finally {
      setLoading(false);
    }
  };

  const fetchEventsByAgeGroup = async () => {
    if (!user?.token) return;
    try {
      setLoading(true);
      const params = {
        organizationId: orgFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined
      };

      const response = await axios.get('/api/reports/events-by-age-group', {
        params,
        headers: { Authorization: `Bearer ${user.token}` }
      });

      setEventsByAgeGroup(response.data.eventsByAgeGroup || []);
    } catch (error) {
      console.error('Error fetching events by age group:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchOpenInvoicesByMonth = async () => {
    if (!user?.token) return;
    try {
      setLoading(true);
      const params = { organizationId: openInvoicesOrg || undefined };
      const response = await axios.get('/api/reports/open-invoices-by-month', {
        params,
        headers: { Authorization: `Bearer ${user.token}` }
      });
      const data = response.data;
      setArSummary(data.summary || null);
      setArByAgeGroup(data.byAgeGroup || []);
      setArByTeam(data.byTeam || []);
      setArByType(data.byType || []);
      setOpenInvoicesByMonth(data.byMonth || []);
      setArInvoices(data.invoices || []);
    } catch (error) {
      console.error('Error fetching outstanding AR:', error);
      toast.error('Failed to load outstanding AR');
    } finally {
      setLoading(false);
    }
  };

  const exportArCsv = () => {
    const headers = ['Invoice #', 'Type', 'User', 'Email', 'Team', 'Age Group', 'Due Date', 'Status', 'Overdue', 'Amount Outstanding'];
    const rows = arInvoices.map(inv => [
      inv.invoiceNumber ?? '',
      inv.invoiceType ?? '',
      inv.user?.name ?? '',
      inv.user?.email ?? '',
      inv.team?.name ?? '',
      inv.ageGroup ?? '',
      inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '',
      inv.status ?? '',
      inv.isOverdue ? 'Yes' : 'No',
      (inv.amountOutstanding || 0).toFixed(2),
    ]);
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `outstanding-ar-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('AR report exported to CSV');
  };

  const fetchUsersReport = async () => {
    if (!user?.token) return;
    try {
      setLoading(true);
      const params = {
        organizationId: usersReportOrg || undefined,
        ageGroup: usersReportAgeGroup || undefined
      };
      const response = await axios.get('/api/reports/users', {
        params,
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setUsersReport(response.data.users || []);
      setUsersReportAgeGroups(response.data.ageGroups || []);
    } catch (error) {
      console.error('Error fetching users report:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const exportUsersCsv = () => {
    const headers = ['Name', 'Email', 'Phone', 'Age', 'Role', 'Age Group', 'Teams'];
    const rows = usersReport.map(u => [
      u.name ?? '',
      u.email ?? '',
      u.phone ?? '',
      u.age ?? '',
      u.role ?? '',
      u.ageGroups ?? '',
      u.teamNames ?? ''
    ]);
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Users exported to CSV');
  };

  const exportTryoutData = () => {
    // Simple CSV export
    const csvRows = [];
    csvRows.push(['Event', 'Name', 'Email', 'Phone', 'Age', 'Status', 'Amount', 'Registered Date']);
    
    tryoutData.forEach(tryout => {
      tryout.registrations.forEach(reg => {
        csvRows.push([
          tryout.event.title,
          reg.name,
          reg.email,
          reg.phone || '',
          reg.age || '',
          reg.status,
          reg.amount || 0,
          new Date(reg.registeredAt).toLocaleDateString()
        ]);
      });
    });

    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tryout-registrations-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Data exported');
  };

  const getUniqueAgeGroups = () => {
    const ageGroups = new Set();
    tryoutData.forEach(tryout => {
      tryout.registrations.forEach(reg => {
        if (reg.team?.ageGroup) {
          ageGroups.add(reg.team.ageGroup);
        }
      });
    });
    return Array.from(ageGroups).sort();
  };

  const getUniqueEvents = () => {
    return tryoutData.map(t => ({
      id: t.event.id,
      title: t.event.title,
      date: t.event.date
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">League Reports & Analytics</h1>
          <p className="mt-2 text-gray-600">Track tryout registrations and event engagement by age group</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('tryouts')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'tryouts'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <UserGroupIcon className="inline h-5 w-5 mr-2" />
              Tryout Registrations
            </button>
            <button
              onClick={() => setActiveTab('events')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'events'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ChartBarIcon className="inline h-5 w-5 mr-2" />
              Events by Age Group
            </button>
            <button
              onClick={() => setActiveTab('invoices')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'invoices'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CurrencyDollarIcon className="inline h-5 w-5 mr-2" />
              Outstanding AR
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'users'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <UsersIcon className="inline h-5 w-5 mr-2" />
              Users
            </button>
          </nav>
        </div>

        {/* Tryout Registrations Tab */}
        {activeTab === 'tryouts' && (
          <div>
            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization
                  </label>
                  <select
                    value={selectedOrg}
                    onChange={(e) => {
                      setSelectedOrg(e.target.value);
                      setSelectedEvent('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Organizations</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tryout Event
                  </label>
                  <select
                    value={selectedEvent}
                    onChange={(e) => setSelectedEvent(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={!selectedOrg}
                  >
                    <option value="">All Tryouts</option>
                    {getUniqueEvents().map(event => (
                      <option key={event.id} value={event.id}>
                        {event.title} ({new Date(event.date).toLocaleDateString()})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Age Group
                  </label>
                  <select
                    value={selectedAgeGroup}
                    onChange={(e) => setSelectedAgeGroup(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Age Groups</option>
                    {getUniqueAgeGroups().map(ag => (
                      <option key={ag} value={ag}>{ag}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Status
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">All Statuses</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={exportTryoutData}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  disabled={tryoutData.length === 0}
                >
                  <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                  Export CSV
                </button>
              </div>
            </div>

            {/* Results */}
            {loading ? (
              <div className="text-center py-12">
                <ArrowPathIcon className="h-8 w-8 animate-spin mx-auto text-primary-600" />
                <p className="mt-2 text-gray-600">Loading registrations...</p>
              </div>
            ) : tryoutData.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No tryout registrations found</p>
              </div>
            ) : (
              <div className="space-y-6">
                {tryoutData.map(tryout => (
                  <div key={tryout.event.id} className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{tryout.event.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {tryout.event.organization?.name || tryout.event.team?.name} • {new Date(tryout.event.date).toLocaleDateString()} {tryout.event.time && `at ${tryout.event.time}`}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-primary-600">{tryout.stats.total}</div>
                          <div className="text-sm text-gray-600">Total Registrations</div>
                        </div>
                      </div>
                    </div>

                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="font-medium text-gray-900">{tryout.stats.paid}</div>
                          <div className="text-gray-600">Paid</div>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{tryout.stats.pending}</div>
                          <div className="text-gray-600">Pending</div>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{tryout.stats.cancelled}</div>
                          <div className="text-gray-600">Cancelled</div>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">${tryout.stats.totalRevenue.toFixed(2)}</div>
                          <div className="text-gray-600">Revenue</div>
                        </div>
                      </div>
                    </div>

                    {tryout.registrations.length > 0 && (
                      <div className="px-6 py-4">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Age</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registered</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {tryout.registrations.map(reg => (
                                <tr key={reg.id}>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{reg.name}</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{reg.email}</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{reg.phone || '-'}</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{reg.age || '-'}</td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                      reg.status === 'paid' ? 'bg-green-100 text-green-800' :
                                      reg.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {reg.status}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">${parseFloat(reg.amount || 0).toFixed(2)}</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                    {new Date(reg.registeredAt).toLocaleDateString()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Events by Age Group Tab */}
        {activeTab === 'events' && (
          <div>
            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization
                  </label>
                  <select
                    value={orgFilter}
                    onChange={(e) => setOrgFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Organizations</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>

            {/* Results */}
            {loading ? (
              <div className="text-center py-12">
                <ArrowPathIcon className="h-8 w-8 animate-spin mx-auto text-primary-600" />
                <p className="mt-2 text-gray-600">Loading events...</p>
              </div>
            ) : eventsByAgeGroup.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No events found</p>
              </div>
            ) : (
              <div className="space-y-6">
                {eventsByAgeGroup.map(group => (
                  <div key={group.ageGroup} className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200 bg-primary-50">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900">{group.ageGroup} Age Group</h3>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-primary-600">{group.stats.total}</div>
                          <div className="text-sm text-gray-600">Total Events</div>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex flex-wrap gap-4 text-sm">
                        {Object.entries(group.stats.byType).map(([type, count]) => (
                          <div key={type}>
                            <span className="font-medium text-gray-900 capitalize">{type}:</span>{' '}
                            <span className="text-gray-600">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="px-6 py-4">
                      <div className="space-y-3">
                        {group.events.map(event => (
                          <div key={event.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{event.title}</div>
                              <div className="text-sm text-gray-600 mt-1">
                                {event.team?.name || event.organization?.name} • {new Date(event.date).toLocaleDateString()} {event.time && `at ${event.time}`}
                                {event.location && ` • ${event.location}`}
                              </div>
                            </div>
                            <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 capitalize">
                              {event.type}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Outstanding AR Tab */}
        {activeTab === 'invoices' && (
          <div>
            {/* Filter + Export bar */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Organization</label>
                  <select
                    value={openInvoicesOrg}
                    onChange={(e) => setOpenInvoicesOrg(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Organizations</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={exportArCsv}
                  disabled={arInvoices.length === 0}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                  Export CSV
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <ArrowPathIcon className="h-8 w-8 animate-spin mx-auto text-primary-600" />
                <p className="mt-2 text-gray-600">Loading outstanding AR...</p>
              </div>
            ) : !arSummary || arSummary.count === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <CurrencyDollarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No outstanding invoices found</p>
              </div>
            ) : (
              <div className="space-y-6">

                {/* Summary cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: 'Total Outstanding', value: `$${arSummary.total.toFixed(2)}`, sub: `${arSummary.count} invoice${arSummary.count !== 1 ? 's' : ''}`, color: 'blue' },
                    { label: 'Overdue', value: `$${arSummary.overdueTotal.toFixed(2)}`, sub: `${arSummary.overdueCount} invoice${arSummary.overdueCount !== 1 ? 's' : ''}`, color: 'red' },
                    { label: 'Current', value: `$${arSummary.currentTotal.toFixed(2)}`, sub: `${arSummary.count - arSummary.overdueCount} invoice${(arSummary.count - arSummary.overdueCount) !== 1 ? 's' : ''}`, color: 'green' },
                    { label: 'Overdue Rate', value: arSummary.total > 0 ? `${((arSummary.overdueTotal / arSummary.total) * 100).toFixed(0)}%` : '0%', sub: 'of total AR', color: 'yellow' },
                  ].map(card => (
                    <div key={card.label} className={`bg-white rounded-lg shadow p-5 border-l-4 ${card.color === 'blue' ? 'border-blue-500' : card.color === 'red' ? 'border-red-500' : card.color === 'green' ? 'border-green-500' : 'border-yellow-500'}`}>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{card.label}</p>
                      <p className={`mt-1 text-2xl font-bold ${card.color === 'red' ? 'text-red-600' : card.color === 'green' ? 'text-green-600' : 'text-gray-900'}`}>{card.value}</p>
                      <p className="mt-1 text-xs text-gray-500">{card.sub}</p>
                    </div>
                  ))}
                </div>

                {/* Breakdowns row */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                  {/* By Age Group */}
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">By Age Group</h3>
                    </div>
                    <table className="min-w-full divide-y divide-gray-100">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Group</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">#</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Overdue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {arByAgeGroup.map(row => (
                          <tr key={row.ageGroup} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.ageGroup}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 text-right">{row.count}</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">${row.total.toFixed(2)}</td>
                            <td className="px-4 py-3 text-sm text-right">
                              {row.overdueTotal > 0
                                ? <span className="text-red-600 font-medium">${row.overdueTotal.toFixed(2)}</span>
                                : <span className="text-gray-400">—</span>}
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-gray-50 font-semibold">
                          <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">{arSummary.count}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">${arSummary.total.toFixed(2)}</td>
                          <td className="px-4 py-3 text-sm text-red-600 text-right">${arSummary.overdueTotal.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* By Team */}
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">By Team</h3>
                    </div>
                    <div className="overflow-y-auto max-h-80">
                      <table className="min-w-full divide-y divide-gray-100">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Team</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">#</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Overdue</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {arByTeam.map(row => (
                            <tr key={row.teamId ?? row.teamName} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-gray-900">{row.teamName}</div>
                                {row.ageGroup && <div className="text-xs text-gray-400">{row.ageGroup}</div>}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-right">{row.count}</td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">${row.total.toFixed(2)}</td>
                              <td className="px-4 py-3 text-sm text-right">
                                {row.overdueTotal > 0
                                  ? <span className="text-red-600 font-medium">${row.overdueTotal.toFixed(2)}</span>
                                  : <span className="text-gray-400">—</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* By Invoice Type */}
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">By Invoice Type</h3>
                    </div>
                    <table className="min-w-full divide-y divide-gray-100">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">#</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Overdue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {arByType.map(row => (
                          <tr key={row.invoiceType} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.invoiceType}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 text-right">{row.count}</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">${row.total.toFixed(2)}</td>
                            <td className="px-4 py-3 text-sm text-right">
                              {row.overdueTotal > 0
                                ? <span className="text-red-600 font-medium">${row.overdueTotal.toFixed(2)}</span>
                                : <span className="text-gray-400">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Detail by month */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Detail by Due Month</h3>
                  <div className="space-y-4">
                    {openInvoicesByMonth.map(group => (
                      <div key={group.monthKey} className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                          <div>
                            <h4 className="text-base font-semibold text-gray-900">{group.monthLabel}</h4>
                            <p className="text-sm text-gray-500 mt-0.5">
                              {group.invoices.length} invoice{group.invoices.length !== 1 ? 's' : ''} · ${group.invoices.reduce((s, i) => s + (i.amountOutstanding || 0), 0).toFixed(2)} outstanding
                            </p>
                          </div>
                          {group.invoices.some(i => i.isOverdue) && (
                            <span className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">
                              {group.invoices.filter(i => i.isOverdue).length} overdue
                            </span>
                          )}
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Age Group</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {group.invoices.map(inv => (
                                <tr key={inv.id} className={inv.isOverdue ? 'bg-red-50' : ''}>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{inv.invoiceNumber ?? '—'}</td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{inv.user?.name ?? '—'}</div>
                                    {inv.user?.email && <div className="text-xs text-gray-400">{inv.user.email}</div>}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{inv.team?.name ?? '—'}</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{inv.ageGroup ?? '—'}</td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${inv.invoiceType === 'Tryout Fee' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'}`}>
                                      {inv.invoiceType}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                    {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '—'}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize ${inv.isOverdue ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                                      {inv.isOverdue && inv.status !== 'overdue' ? 'overdue' : inv.status}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                                    ${(inv.amountOutstanding || 0).toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization
                  </label>
                  <select
                    value={usersReportOrg}
                    onChange={(e) => setUsersReportOrg(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Organizations</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Age Group
                  </label>
                  <select
                    value={usersReportAgeGroup}
                    onChange={(e) => setUsersReportAgeGroup(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Age Groups</option>
                    {usersReportAgeGroups.map(ag => (
                      <option key={ag} value={ag}>{ag}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={exportUsersCsv}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    disabled={usersReport.length === 0}
                  >
                    <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                    Export CSV
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <ArrowPathIcon className="h-8 w-8 animate-spin mx-auto text-primary-600" />
                <p className="mt-2 text-gray-600">Loading users...</p>
              </div>
            ) : usersReport.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No users found</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Age</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Age Group</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teams</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {usersReport.map(u => (
                        <tr key={u.id}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{u.name ?? '—'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{u.email ?? '—'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{u.phone ?? '—'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{u.age ?? '—'}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 capitalize">
                              {u.role ?? '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{u.ageGroups || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{u.teamNames || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeagueReports;

